import React, { useState, useEffect } from 'react';
import { LuBaby, LuMapPin, LuPhone, LuLoader, LuMessageCircle } from 'react-icons/lu';
import { GiChurch } from 'react-icons/gi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function ChildCare() {
  const [homesList, setHomesList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchHomes() {
      try {
        setLoading(true);
        let querySnapshot;
        try {
          querySnapshot = await getDocs(collection(db, 'home'));
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'homes'));
          }
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'childcare'));
          }
        } catch (e) {
          try {
            querySnapshot = await getDocs(collection(db, 'homes'));
          } catch (e2) {
            querySnapshot = await getDocs(collection(db, 'home'));
          }
        }

        const fetched = [];
        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const name = data.home_name || data.name || data.title || "CSI Child Care Home";
            const address = data.address || data.location || "";
            const contact = data.phone || data.mobile || data.contact || "";
            const order = typeof data.order === 'number' ? data.order : 999;
            const incharge = data.incharge || data.warden || data.manager || "";

            fetched.push({
              id: doc.id,
              name: name.toUpperCase(),
              address,
              contact,
              order,
              incharge,
              raw: data
            });
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setHomesList(fetched);
        }
      } catch (err) {
        console.error("Error fetching child care homes from Firebase:", err);
        if (isMounted) setHomesList([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchHomes();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col items-center md:items-start text-center md:text-left">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full" />
          <h1 className="font-serif font-bold text-[#7B1E3A] text-[38px] sm:text-[50px] lg:text-[56px] uppercase tracking-[0.03em] leading-[1.05]">
            Child Care &amp; Life Skill Centers
          </h1>
          <p className="mt-4 max-w-xl text-[#7B6B66] text-[16px] sm:text-[17px] leading-[1.6]">
            Compassionate care, holistic development, residential support, and life skill training for children across the Malabar Diocese.
          </p>
        </div>
      </section>

      {/* Items Grid */}
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-12 w-full flex-grow">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading child care homes...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {homesList.map((item, idx) => {
              const cleanPhone = item.contact ? item.contact.replace(/\D/g, '') : '';

              return (
                <div
                  key={item.id || idx}
                  className="bg-white rounded-[24px] border border-[#E8DDD7] shadow-sm hover:shadow-[0_15px_40px_rgba(123,30,58,0.10)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between p-7 group h-full"
                >
                  <div>
                    {/* Icon & Category Badge */}
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <div className="w-12 h-12 rounded-2xl bg-[#FAF6F2] flex items-center justify-center text-[#7B1E3A] group-hover:bg-[#7B1E3A] group-hover:text-white transition-colors duration-300 shrink-0 border border-[#E8DDD7]">
                        <LuBaby className="w-6 h-6" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#842343] bg-[#FAF0F2] px-2.5 py-1 rounded-full border border-[#F2D5DC]">
                        Diocesan Home
                      </span>
                    </div>

                    <h3 className="font-serif font-bold text-[#1a1715] text-[17px] sm:text-[18px] leading-snug mb-4 group-hover:text-[#7B1E3A] transition-colors min-h-[44px]">
                      {item.name}
                    </h3>
                  </div>
                  
                  <div className="space-y-4 pt-3 border-t border-[#F0EBE6]">
                    {/* Contact Phone */}
                    {item.contact && (
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5 text-[#7B1E3A] font-bold text-[14px]">
                          <div className="w-8 h-8 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 border border-[#E8DDD7]">
                            <LuPhone className="w-4 h-4" />
                          </div>
                          <a href={`tel:${item.contact}`} className="hover:underline">
                            {item.contact}
                          </a>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <a
                            href={`tel:${item.contact}`}
                            className="p-2 rounded-xl bg-[#842343] hover:bg-[#6c1b35] text-white text-xs transition-colors"
                            title="Call Home"
                          >
                            <LuPhone className="w-3.5 h-3.5" />
                          </a>
                          {cleanPhone && (
                            <a
                              href={`https://wa.me/91${cleanPhone}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs transition-colors"
                              title="Chat on WhatsApp"
                            >
                              <LuMessageCircle className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Address */}
                    {item.address && (
                      <div className="flex items-start gap-2.5 text-[#5F5A58] text-[13px] pt-1">
                        <div className="w-7 h-7 rounded-full bg-[#FAF6F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                          <LuMapPin className="w-3.5 h-3.5 text-[#D4A64A]" />
                        </div>
                        <span className="leading-relaxed">{item.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
